import { musician } from '@/mocks/musician';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Contact() {
  useScrollAnimation();

  const contactItems = [
    {
      icon: 'ri-phone-line',
      label: 'Phone',
      value: musician.phone,
      href: `tel:${musician.phone}`,
      showValue: true,
    },
    {
      icon: 'ri-mail-line',
      label: 'Email',
      value: musician.email,
      href: `mailto:${musician.email}`,
      showValue: true,
    },
    {
      icon: 'ri-instagram-line',
      label: 'Instagram',
      value: '@jacksonlacefieldmusic',
      href: musician.instagramUrl,
      showValue: true,
      external: true,
    },
    {
      icon: 'ri-youtube-line',
      label: 'YouTube',
      value: '@jacksonlacefield',
      href: musician.youtubeChannelUrl,
      showValue: true,
      external: true,
    },
  ];

  return (
    <>
      <title>Contact Jackson Lacefield | Tenor &amp; Worship Musician</title>
      <meta name="description" content="Get in touch with Jackson Lacefield for performance inquiries, worship leading, or collaboration. Tenor and multi-instrumentalist based in Dallas / Ft. Worth and Waco, Texas." />
      <meta property="og:title" content="Contact Jackson Lacefield | Tenor &amp; Worship Musician" />
      <meta property="og:description" content="Reach out to Jackson Lacefield for worship leading, performance bookings, and musical collaboration." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.jacksonlacefield.com/contact" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Contact Jackson Lacefield | Tenor &amp; Worship Musician" />
      <meta name="twitter:description" content="Reach out to Jackson Lacefield for worship leading, performance bookings, and musical collaboration." />

      <main className="min-h-screen bg-background-50">
        {/* ── Page Header ── */}
        <section className="pt-28 md:pt-32 pb-12 md:pb-16 gradient-section-orange film-grain">
          <div className="section-container text-center relative z-10">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4 scroll-animate">
              Contact
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-900 mb-3 scroll-animate">
              Get in Touch
            </h1>
            <p className="font-body text-sm md:text-base text-foreground-500 max-w-lg mx-auto scroll-animate">
              For performance inquiries, worship leading, or collaboration &mdash; reach out directly.
            </p>
          </div>
        </section>

        {/* ── Contact Cards ── */}
        <section className="py-12 md:pt-20 md:pb-10 gradient-section-mid film-grain section-popout">
          <div className="section-container max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 scroll-animate">
              {contactItems.map((item, i) => {
                const linkProps = item.external
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {};
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    {...linkProps}
                    className="group flex items-center gap-5 md:gap-6 popout-card px-6 py-5 md:px-10 md:py-8 transition-all duration-500 cursor-pointer"
                    style={{ transitionDelay: `${i * 0.08}s` }}
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-accent-100 flex items-center justify-center text-accent-500 flex-shrink-0 transition-all duration-500 group-hover:bg-accent-500 group-hover:text-background-50 group-hover:scale-105">
                      <i className={`${item.icon} text-xl md:text-2xl`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-label text-xs text-foreground-400 mb-0.5 tracking-wide">{item.label}</p>
                      {item.showValue && (
                        <p className="font-body text-sm md:text-base lg:text-lg text-foreground-700 group-hover:text-accent-500 transition-colors duration-500 font-medium truncate">
                          {item.value}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-500">
                      <i className="ri-arrow-right-up-line text-lg text-accent-500"></i>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}